import { UseFormReturn } from "react-hook-form";
import { DrawerClose, DrawerHeader } from "@/components/ui/drawer";
import { FormField } from "@/components/ui/form";
import { Step } from "./constants";
import { cn, formatDate } from "@/lib/utils";
import { CaretRight } from "@phosphor-icons/react";
import { Button, buttonVariants } from "@/components/ui/button";
import FilePreview from "@/components/custom/file-preview";
import { useTranslation } from "react-i18next";
import { useEffect, useMemo, useState } from "react";
import ScrollableCalendar from "@/components/custom/scrollable-calendar";
import { Plus, X } from "lucide-react";
import { Vendor, Currency, Vessel, Budget } from "@/lib/types";
import { InvoiceFormValues } from "./index";
import { INVOICE_STATUS } from "@/lib/constants";

const InitialStep = ({
  form,
  setStep,
  currencies,
  vessels,
  budgets,
  vendors,
  title,
  isSubmitting,
}: {
  form: UseFormReturn<InvoiceFormValues>;
  setStep: React.Dispatch<React.SetStateAction<number>>;
  currencies: Currency[];
  vessels: Vessel[];
  budgets: Budget[];
  vendors: Vendor[];
  title?: string;
  /** Disables Save while a submission is in flight to prevent duplicate invoices. */
  isSubmitting?: boolean;
}) => {
  const { t } = useTranslation();

  return (
    <>
      <DrawerHeader
        title={title ?? t("title.add_invoice")}
        leftButton={
          <DrawerClose asChild>
            <button
              type="button"
              className="text-[15px] font-medium leading-5 text-[#45D3EC]"
            >
              {t("button.cancel")}
            </button>
          </DrawerClose>
        }
        rightButton={
          <button
            data-testid="invoice-form-submit-button"
            type="submit"
            disabled={isSubmitting}
            className="text-[15px] font-medium leading-5 text-[#45D3EC] disabled:opacity-50"
          >
            {t("button.save")}
          </button>
        }
      />

      <div className="mt-[30px] w-full space-y-[15px]">
        <VendorButton form={form} setStep={setStep} vendors={vendors} />
        <CostCenterButton vessels={vessels} form={form} setStep={setStep} />
        <BudgetButton budgets={budgets} form={form} setStep={setStep} />
        <CurrencyButton currencies={currencies} form={form} setStep={setStep} />
        <InvoiceNumberButton form={form} setStep={setStep} />
        <StatusButton form={form} setStep={setStep} />
        <IssueDateButton form={form} />
        <DueDateButton form={form} />
        <UploadFilesButton form={form} />
        <NoteButton form={form} setStep={setStep} />
      </div>
    </>
  );
};

export default InitialStep;

const StatusButton = ({
  form,
  setStep,
}: {
  form: UseFormReturn<InvoiceFormValues>;
  setStep: React.Dispatch<React.SetStateAction<number>>;
}) => {
  const { t } = useTranslation();

  return (
    <>
      {form.getValues("status") ? (
        <button
          data-testid="invoice-form-status-button"
          type="button"
          onClick={() => setStep(Step.STATUS)}
          className="flex w-full flex-col items-start justify-center rounded-lg bg-[#1FB3CC1F] px-[15px] py-[10px] shadow-[inset_0px_1px_2px_0px_#0C0C2633] backdrop-blur-lg"
        >
          <span className="text-xs leading-4 text-white/50">
            {t("label.status")}
          </span>
          <p className="flex items-center gap-1.5 text-start text-[15px] text-white">
            <span>
              {t(
                `invoice_status.${
                  INVOICE_STATUS.find(
                    (status) => status.value === form.watch("status")
                  )?.translationKey
                }`
              )}
            </span>
          </p>
        </button>
      ) : (
        <Button
          data-invalid={!!form.formState.errors.status}
          data-testid="invoice-form-status-button"
          onClick={() => setStep(Step.STATUS)}
          size="lg"
          variant="outline"
          className={cn("flex w-full items-center justify-between gap-2", {
            "border-red-500 hover:border-red-500 active:border-red-500":
              form.formState.errors.status,
          })}
        >
          {t("label.status")}
          <CaretRight width={10} color="#E3EDFC" />
        </Button>
      )}
    </>
  );
};

const CurrencyButton = ({
  currencies,
  form,
  setStep,
}: {
  currencies: Currency[];
  form: UseFormReturn<InvoiceFormValues>;
  setStep: React.Dispatch<React.SetStateAction<number>>;
}) => {
  const { t } = useTranslation();

  const currency = useMemo(
    () =>
      currencies.find((currency) => currency.id === form.watch("currencyId")),
    [currencies, form]
  );

  return (
    <>
      {form.getValues("currencyId") ? (
        <button
          data-testid="invoice-form-currency-button"
          type="button"
          onClick={() => setStep(Step.CURRENCY)}
          className="flex w-full flex-col items-start justify-center rounded-lg bg-[#1FB3CC1F] px-[15px] py-[10px] shadow-[inset_0px_1px_2px_0px_#0C0C2633] backdrop-blur-lg"
        >
          <span className="text-xs leading-4 text-white/50">
            {t("label.currency")}
          </span>
          <p className="flex items-center gap-1.5 text-start text-[15px] text-white">
            <span>{currency?.symbol}</span>
            <span>{t(`currency.${currency?.code.toLocaleLowerCase()}`)}</span>
          </p>
        </button>
      ) : (
        <Button
          data-invalid={!!form.formState.errors.currencyId}
          data-testid="invoice-form-currency-button"
          onClick={() => setStep(Step.CURRENCY)}
          size="lg"
          variant="outline"
          className={cn("flex w-full items-center justify-between gap-2", {
            "border-red-500 hover:border-red-500 active:border-red-500":
              form.formState.errors.currencyId,
          })}
        >
          {t("label.currency")}
          <CaretRight width={10} color="#E3EDFC" />
        </Button>
      )}
    </>
  );
};

const VendorButton = ({
  form,
  setStep,
  vendors,
}: {
  form: UseFormReturn<InvoiceFormValues>;
  setStep: React.Dispatch<React.SetStateAction<number>>;
  vendors: Vendor[];
}) => {
  const { t } = useTranslation();
  const [isEnteringVendor, setIsEnteringVendor] = useState(false);
  const vendor = form.watch("vendor");

  const isEnteringVendorExists = useMemo(() => {
    return (
      typeof vendor === "string" &&
      vendors.find((v) => v.name.toLowerCase() === vendor.toLowerCase())
    );
  }, [vendor, vendors]);

  useEffect(() => {
    if (typeof vendor === "string") {
      setIsEnteringVendor(true);
    }
  }, [vendor]);

  return (
    <div className="flex flex-row items-center justify-between gap-3">
      {!isEnteringVendor ? (
        (vendor as { id: string })?.id ? (
          <button
            data-testid="invoice-form-vendor-button"
            type="button"
            onClick={() => setStep(Step.VENDOR)}
            className="flex w-full flex-row items-center justify-between rounded-lg bg-[#1FB3CC1F] px-[15px] py-[10px] shadow-[inset_0px_1px_2px_0px_#0C0C2633] backdrop-blur-lg"
          >
            <div className="flex flex-col items-start justify-between">
              <span className="text-xs leading-4 text-white/50">
                {t("label.supplier")}
              </span>
              <p className="text-start text-[15px] text-white">
                {
                  vendors.find((v) => v.id === (vendor as { id: string }).id)
                    ?.name
                }
              </p>
            </div>

            <div
              role="button"
              onClick={(e) => {
                e.stopPropagation();
                form.setValue("vendor", null);
              }}
            >
              <X size={16} color="#E3EDFC" />
            </div>
          </button>
        ) : (
          <Button
            data-invalid={!!form.formState.errors.vendor}
            data-testid="invoice-form-vendor-button"
            onClick={() => setStep(Step.VENDOR)}
            size="lg"
            variant="outline"
            className={cn("flex w-full items-center justify-between gap-2", {
              "border-red-500 hover:border-red-500 active:border-red-500":
                form.formState.errors.vendor,
            })}
          >
            {t("label.supplier")}
            <CaretRight width={10} color="#E3EDFC" />
          </Button>
        )
      ) : (
        <input
          data-testid="invoice-form-new-vendor-input"
          className={cn(
            "h-[43px] w-full rounded-lg border border-solid border-white/[.33] bg-transparent px-[15px] py-[10px] text-white outline-none",
            {
              "border-red-500 hover:border-red-500 active:border-red-500":
                form.formState.errors.vendor,
            }
          )}
          placeholder={t("label.enter_new_supplier_name")}
          value={form.watch("vendor")?.toString() ?? ""}
          onChange={(e) => {
            form.setValue("vendor", e.target.value);
            form.trigger("vendor");
          }}
        />
      )}

      {(typeof vendor === "string" || !vendor) && (
        <div className="flex flex-row items-center justify-between gap-2">
          {isEnteringVendor && (
            <Button
              disabled={
                !(
                  typeof form.watch("vendor") === "string" &&
                  (form.watch("vendor")?.toString().length ?? 0) > 0
                )
              }
              onClick={() => {
                if (isEnteringVendorExists) {
                  setIsEnteringVendor(false);
                  form.setValue("vendor", isEnteringVendorExists);
                } else {
                  setStep(Step.VENDOR_PAYMENT_DETAILS);
                }
              }}
              data-testid="invoice-form-add-new-vendor-button"
              type="button"
              className="h-[43px] w-[43px] min-w-[43px] items-center"
            >
              <Plus size={16} color="#E3EDFC" />
            </Button>
          )}
          <button
            data-testid={
              !isEnteringVendor
                ? "invoice-form-add-new-vendor-button"
                : "invoice-form-cancel-new-vendor-button"
            }
            type="button"
            onClick={() => {
              setIsEnteringVendor(!isEnteringVendor);
              form.setValue("vendor", null);

              // Clear the vendor payment methods when entering a new vendor input is disappeared
              if (isEnteringVendor) {
                form.setValue("vendorPaymentMethods", []);
              }
            }}
            className="flex h-[43px] w-[43px] min-w-[43px] items-center justify-center rounded-lg border border-solid border-white/[.33]"
          >
            {!isEnteringVendor ? (
              <Plus size={16} color="#E3EDFC" />
            ) : (
              <X size={16} color="#E3EDFC" />
            )}
          </button>
        </div>
      )}
    </div>
  );
};

const BudgetButton = ({
  form,
  setStep,
  budgets,
}: {
  form: UseFormReturn<InvoiceFormValues>;
  setStep: React.Dispatch<React.SetStateAction<number>>;
  budgets: Budget[];
}) => {
  const { t } = useTranslation();

  const budget = useMemo(
    () => budgets.find((budget) => budget.id === form.watch("budgetId")),
    [budgets, form]
  );

  return (
    <>
      {budget?.id ? (
        <button
          data-testid="invoice-form-budget-button"
          type="button"
          onClick={() => setStep(Step.BUDGET)}
          className="flex w-full flex-col items-start justify-center rounded-lg bg-[#1FB3CC1F] px-[15px] py-[10px] shadow-[inset_0px_1px_2px_0px_#0C0C2633] backdrop-blur-lg"
        >
          <span className="text-xs leading-4 text-white/50">
            {t("label.budget")}
          </span>
          <p className="text-start text-[15px] text-white">{budget?.name}</p>
        </button>
      ) : (
        <Button
          data-invalid={!!form.formState.errors.budgetId}
          data-testid="invoice-form-budget-button"
          onClick={() => setStep(Step.BUDGET)}
          size="lg"
          variant="outline"
          className={cn("flex w-full items-center justify-between gap-2", {
            "border-red-500 hover:border-red-500 active:border-red-500":
              form.formState.errors.budgetId,
          })}
        >
          {t("label.budget")}
          <CaretRight width={10} color="#E3EDFC" />
        </Button>
      )}
    </>
  );
};

const IssueDateButton = ({
  form,
}: {
  form: UseFormReturn<InvoiceFormValues>;
}) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="relative w-full">
      <div
        data-invalid={!!form.formState.errors.issueDate}
        data-testid="invoice-form-issue-date-button"
        role="button"
        onClick={() => setIsOpen(!isOpen)}
        className={cn(
          form.getValues("issueDate")
            ? "flex w-full flex-col items-start justify-center rounded-lg bg-[#1FB3CC1F] px-[15px] py-[10px] shadow-[inset_0px_1px_2px_0px_#0C0C2633] backdrop-blur-lg"
            : `flex w-full flex-row items-center !justify-between gap-2 ${buttonVariants(
                {
                  variant: "outline",
                  size: "lg",
                }
              )}`,
          {
            "border-red-500 hover:border-red-500 active:border-red-500":
              form.formState.errors.issueDate,
          }
        )}
      >
        {form.getValues("issueDate") ? (
          <>
            <span className="text-xs leading-4 text-white/50">
              {t("label.issue_date")}
            </span>
            <span className="text-start text-[15px] text-white">
              {formatDate(form.getValues("issueDate")!, "dd MMMM yyyy - HH:mm")}
            </span>
          </>
        ) : (
          <div className="flex w-full flex-row items-center justify-between gap-2">
            {t("label.issue_date")}
            <CaretRight width={10} color="#E3EDFC" />
          </div>
        )}
      </div>

      {isOpen && (
        <ScrollableCalendar
          data-testid="invoice-form-issue-date-calendar"
          date={
            form.getValues("issueDate")
              ? new Date(form.getValues("issueDate")!)
              : new Date()
          }
          setDate={(date) => {
            form.setValue("issueDate", date);
            form.trigger("issueDate");
          }}
        />
      )}
    </div>
  );
};

const DueDateButton = ({
  form,
}: {
  form: UseFormReturn<InvoiceFormValues>;
}) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="relative w-full">
      <div
        data-invalid={!!form.formState.errors.dueDate}
        data-testid="invoice-form-due-date-button"
        role="button"
        onClick={() => setIsOpen(!isOpen)}
        className={cn(
          form.getValues("dueDate")
            ? "flex w-full flex-col items-start justify-center rounded-lg bg-[#1FB3CC1F] px-[15px] py-[10px] shadow-[inset_0px_1px_2px_0px_#0C0C2633] backdrop-blur-lg"
            : `flex w-full flex-row items-center !justify-between gap-2 ${buttonVariants(
                {
                  variant: "outline",
                  size: "lg",
                }
              )}`,
          {
            "border-red-500 hover:border-red-500 active:border-red-500":
              form.formState.errors.dueDate,
          }
        )}
      >
        {form.getValues("dueDate") ? (
          <>
            <span className="text-xs leading-4 text-white/50">
              {t("label.due_date")}
            </span>
            <span className="text-start text-[15px] text-white">
              {formatDate(form.getValues("dueDate")!, "dd MMMM yyyy - HH:mm")}
            </span>
          </>
        ) : (
          <div className="flex w-full flex-row items-center justify-between gap-2">
            {t("label.due_date")}
            <CaretRight width={10} color="#E3EDFC" />
          </div>
        )}
      </div>

      {isOpen && (
        <ScrollableCalendar
          data-testid="invoice-form-due-date-calendar"
          date={
            form.getValues("dueDate")
              ? new Date(form.getValues("dueDate")!)
              : new Date()
          }
          setDate={(date) => {
            form.setValue("dueDate", date);
            form.trigger("dueDate");
          }}
        />
      )}
    </div>
  );
};

const UploadFilesButton = ({
  form,
}: {
  form: UseFormReturn<InvoiceFormValues>;
}) => {
  const { t } = useTranslation();
  return (
    <>
      <label
        data-invalid={!!form.formState.errors.files}
        data-testid="invoice-form-files-label"
        htmlFor="invoice-files"
        className={cn(
          form.getValues("files") && form.getValues("files").length > 0
            ? "flex w-full flex-col items-start justify-center rounded-lg bg-[#1FB3CC1F] px-[15px] py-[10px] shadow-[inset_0px_1px_2px_0px_#0C0C2633] backdrop-blur-lg"
            : `flex w-full flex-row items-center justify-between gap-2 ${buttonVariants(
                {
                  variant: "outline",
                  size: "lg",
                }
              )}`,
          {
            "border-red-500 hover:border-red-500 active:border-red-500":
              form.formState.errors.files,
          }
        )}
      >
        {form.getValues("files").length > 0 ? (
          <>
            <span className="text-xs leading-4 text-white/50">
              {t("label.files")}
            </span>
            <FilePreview
              className="mt-1"
              files={
                "url" in form.watch("files")[0]
                  ? undefined
                  : (form.watch("files") as unknown as File[])
              }
              urls={
                "url" in form.watch("files")[0]
                  ? (form.watch("files") as { id: string; url: string }[]).map(
                      (file) => file.url
                    )
                  : undefined
              }
              onRemove={(file) => {
                if (typeof file === "string") {
                  form.setValue(
                    "files",
                    (
                      form.watch("files") as { id: string; url: string }[]
                    ).filter((f) => f.url !== file)
                  );
                } else {
                  form.setValue(
                    "files",
                    (form.watch("files") as unknown as File[]).filter(
                      (f) => f !== file
                    ) as unknown as { id: string; url: string }[]
                  );
                }
              }}
            />
          </>
        ) : (
          <div className="flex w-full flex-row items-center justify-between gap-2">
            {t("label.files")}
            <CaretRight width={10} color="#E3EDFC" />
          </div>
        )}
      </label>
      <FormField
        control={form.control}
        name="files"
        render={({ field: { value, onChange, ...rest } }) => (
          <input
            data-testid="invoice-form-files-input"
            id="invoice-files"
            type="file"
            multiple
            accept="image/*,application/pdf"
            hidden
            onChange={(e) => {
              form.watch("files");
              onChange(Array.from(e.target.files || []));
            }}
            {...rest}
          />
        )}
      />
    </>
  );
};

const NoteButton = ({
  form,
  setStep,
}: {
  form: UseFormReturn<InvoiceFormValues>;
  setStep: React.Dispatch<React.SetStateAction<number>>;
}) => {
  const { t } = useTranslation();
  return (
    <>
      {form.getValues("description") &&
      form.getValues("description").trim() !== "" ? (
        <button
          data-testid="invoice-form-note-button"
          type="button"
          onClick={() => setStep(Step.NOTE)}
          className="flex w-full flex-col items-start justify-center rounded-lg bg-[#1FB3CC1F] px-[15px] py-[10px] shadow-[inset_0px_1px_2px_0px_#0C0C2633] backdrop-blur-lg"
        >
          <span className="text-xs leading-4 text-white/50">
            {t("label.notes")}
          </span>
          <p className="text-start text-[15px] text-white">
            {form.getValues("description")}
          </p>
        </button>
      ) : (
        <Button
          data-invalid={!!form.formState.errors.description}
          data-testid="invoice-form-note-button"
          onClick={() => setStep(Step.NOTE)}
          size="lg"
          variant="outline"
          className={cn("w-full", {
            "border-red-500 hover:border-red-500 active:border-red-500":
              form.formState.errors.description,
          })}
        >
          <div className="flex w-full flex-row items-center justify-start gap-2">
            {t("label.add_note")}
            <span className="text-base text-white/50">
              ({t("label.optional")})
            </span>
          </div>
          <CaretRight width={10} color="#E3EDFC" />
        </Button>
      )}
    </>
  );
};

const InvoiceNumberButton = ({
  form,
  setStep,
}: {
  form: UseFormReturn<InvoiceFormValues>;
  setStep: React.Dispatch<React.SetStateAction<number>>;
}) => {
  const { t } = useTranslation();
  return (
    <>
      {form.getValues("invoiceNumber") &&
      form.getValues("invoiceNumber").trim() !== "" ? (
        <button
          data-testid="invoice-form-invoice-number-button"
          type="button"
          onClick={() => setStep(Step.INVOICE_NUMBER)}
          className="flex w-full flex-col items-start justify-center rounded-lg bg-[#1FB3CC1F] px-[15px] py-[10px] shadow-[inset_0px_1px_2px_0px_#0C0C2633] backdrop-blur-lg"
        >
          <span className="text-xs leading-4 text-white/50">
            {t("label.invoice_number")}
          </span>
          <p className="text-start text-[15px] text-white">
            {form.getValues("invoiceNumber")}
          </p>
        </button>
      ) : (
        <Button
          data-invalid={!!form.formState.errors.invoiceNumber}
          data-testid="invoice-form-invoice-number-button"
          onClick={() => setStep(Step.INVOICE_NUMBER)}
          size="lg"
          variant="outline"
          className={cn("w-full", {
            "border-red-500 hover:border-red-500 active:border-red-500":
              form.formState.errors.invoiceNumber,
          })}
        >
          <div className="flex w-full flex-row items-center justify-start gap-2">
            {t("label.add_invoice_number")}
          </div>
          <CaretRight width={10} color="#E3EDFC" />
        </Button>
      )}
    </>
  );
};

const CostCenterButton = ({
  form,
  setStep,
  vessels,
}: {
  form: UseFormReturn<InvoiceFormValues>;
  setStep: React.Dispatch<React.SetStateAction<number>>;
  vessels: Vessel[];
}) => {
  const { t } = useTranslation();
  const costCenter = useMemo(
    () => vessels.find((vessel) => vessel.id === form.watch("vesselId")),
    [vessels, form]
  );

  return (
    <>
      {costCenter?.id ? (
        <button
          data-testid="invoice-form-cost-center-button"
          type="button"
          onClick={() => setStep(Step.COST_CENTER)}
          className="flex w-full flex-col items-start justify-center rounded-lg bg-[#1FB3CC1F] px-[15px] py-[10px] shadow-[inset_0px_1px_2px_0px_#0C0C2633] backdrop-blur-lg"
        >
          <span className="text-xs leading-4 text-white/50">
            {t("label.cost_center")}
          </span>
          <p className="text-start text-[15px] text-white">
            {costCenter?.name}
          </p>
        </button>
      ) : (
        <Button
          data-invalid={!!form.formState.errors.vesselId}
          data-testid="invoice-form-cost-center-button"
          onClick={() => setStep(Step.COST_CENTER)}
          size="lg"
          variant="outline"
          className={cn("flex w-full items-center justify-between gap-2", {
            "border-red-500 hover:border-red-500 active:border-red-500":
              form.formState.errors.vesselId,
          })}
        >
          {t("label.cost_center")}
          <CaretRight width={10} color="#E3EDFC" />
        </Button>
      )}
    </>
  );
};
